import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { EmptyState } from '@/components/ui/EmptyState';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

const PER_PAGE = 20;

type LogRow = {
  id: number;
  site_id: number;
  miniprogram_id: number;
  miniprogram_name: string;
  forward_task_id: number;
  entry_id: number;
  robot_id: number;
  robot_name: string;
  task_title: string;
  pulled_at: string;
  completed_at: string;
};

type ListResp = { ok: boolean; items: LogRow[]; total: number; page: number; per_page: number; q?: string };

type MpBrief = { id: number; name: string; enabled: boolean };

export function RobotForwardLogsPage() {
  const { currentSiteId, loading: sessLoading } = useSession();
  const [searchParams] = useSearchParams();
  const presetMpId = Math.max(
    0,
    Number(searchParams.get('miniprogram_id') || searchParams.get('active_id') || 0) || 0,
  );
  const [items, setItems] = useState<LogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [mpOptions, setMpOptions] = useState<MpBrief[]>([]);
  const [mpDraft, setMpDraft] = useState(presetMpId);
  const [mpApplied, setMpApplied] = useState(presetMpId);
  const [qDraft, setQDraft] = useState('');
  const [qApplied, setQApplied] = useState('');

  const loadMps = useCallback(async () => {
    const r = await apiFetch<{ ok: boolean; items: MpBrief[] }>('list_miniprograms', {
      method: 'GET',
      query: { page: 1, per_page: 200 },
    });
    if (r.ok && Array.isArray(r.items)) {
      setMpOptions(r.items);
    } else {
      setMpOptions([]);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    if (currentSiteId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setMsg('');
    try {
      const r = await apiFetch<ListResp>('list_robot_forward_logs', {
        method: 'GET',
        query: {
          site_id: currentSiteId,
          page,
          per_page: PER_PAGE,
          ...(mpApplied > 0 ? { miniprogram_id: mpApplied } : {}),
          ...(qApplied.trim() ? { q: qApplied.trim() } : {}),
        },
      });
      if (r.ok) {
        setItems(Array.isArray(r.items) ? r.items : []);
        setTotal(typeof r.total === 'number' ? r.total : 0);
      } else {
        setMsg('加载失败');
        setItems([]);
        setTotal(0);
      }
    } catch {
      setMsg('加载失败');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentSiteId, page, mpApplied, qApplied]);

  useEffect(() => {
    if (sessLoading) return;
    void loadMps();
  }, [sessLoading, loadMps]);

  useEffect(() => {
    if (sessLoading) return;
    void loadLogs();
  }, [sessLoading, loadLogs]);

  useEffect(() => {
    const mp = Math.max(0, Number(searchParams.get('miniprogram_id') || searchParams.get('active_id') || 0) || 0);
    setMpDraft(mp);
    setMpApplied(mp);
    setPage(1);
  }, [searchParams]);

  function runSearch() {
    setMpApplied(mpDraft);
    setQApplied(qDraft.trim());
    setPage(1);
  }

  function resetSearch() {
    setMpDraft(0);
    setMpApplied(0);
    setQDraft('');
    setQApplied('');
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  if (sessLoading) {
    return <Spinner label="加载会话" />;
  }

  if (currentSiteId < 1) {
    return <p className="text-sm text-amber-800">请先在上方「当前工作站点」中选择站点后再查看转发日志。</p>;
  }

  return (
    <div className="space-y-6">
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-slate-500">
        <Link to="/miniprograms" className="rounded-md hover:bg-slate-100 hover:text-teal-800">
          小程序管理
        </Link>
        <span className="text-slate-300" aria-hidden>
          /
        </span>
        <span className="font-medium text-slate-800">转发日志</span>
      </nav>

      <PageHeader
        title={mpApplied > 0 ? '小程序 · 转发日志' : '转发日志'}
        description={
          mpApplied > 0
            ? '当前站点下、已筛选指定小程序的 Android 转发队列日志（获取任务 / 群发完成时间）。'
            : '记录当前工作站点下，Android 机器人在转发队列中的任务获取与微信群发完成时间。'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" onClick={() => void loadLogs()} disabled={loading}>
              刷新
            </Button>
            <Link to="/miniprograms">
              <Button type="button" variant="secondary">
                返回小程序管理
              </Button>
            </Link>
          </div>
        }
      />

      {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">{msg}</p> : null}

      <Card title="筛选">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
          <label className={`${labelClass} min-w-[12rem] flex-1`}>
            <span>小程序（可选）</span>
            <select
              className={inputClass}
              value={mpDraft}
              onChange={(e) => setMpDraft(Math.max(0, parseInt(e.target.value, 10) || 0))}
            >
              <option value={0}>全部</option>
              {mpOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                  {!m.enabled ? '（已停用）' : ''} · #{m.id}
                </option>
              ))}
            </select>
          </label>
          <label className={`${labelClass} min-w-[14rem] flex-[2]`}>
            <span>关键词（任务标题 / 机器人名称 / 信息或机器人编号）</span>
            <input
              className={inputClass}
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              placeholder="可选，回车或点击查询"
              spellCheck={false}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="primary" onClick={runSearch} disabled={loading}>
              查询
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={resetSearch}
              disabled={loading || (mpApplied === 0 && !qApplied && mpDraft === 0 && !qDraft.trim())}
            >
              重置
            </Button>
          </div>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">
          获取任务时间由服务端在 <code className="rounded bg-slate-100 px-1">android_tasks</code> 返回队列项时记录；完成任务时间在微信群发成功回传时写入。升级后新产生的转发才会出现完整轨迹，历史数据不追溯补录。
        </p>
      </Card>

      <Card title="记录列表">
        {loading ? (
          <Spinner label="加载中" />
        ) : items.length === 0 ? (
          <EmptyState>暂无转发日志。机器人拉取队列任务并成功群发后，将在此显示。</EmptyState>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="whitespace-nowrap px-3 py-3 font-medium">获取任务时间</th>
                    <th className="whitespace-nowrap px-3 py-3 font-medium">完成任务时间</th>
                    <th className="px-3 py-3 font-medium">机器人</th>
                    <th className="px-3 py-3 font-medium">小程序</th>
                    <th className="min-w-[10rem] px-3 py-3 font-medium">任务标题</th>
                    <th className="whitespace-nowrap px-3 py-3 font-medium">信息</th>
                    <th className="whitespace-nowrap px-3 py-3 font-medium">队列 #</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {items.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600">
                        {row.pulled_at || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 align-top text-xs text-slate-600">
                        {row.completed_at || <span className="text-amber-700">进行中</span>}
                      </td>
                      <td className="px-3 py-3 align-top text-sm text-slate-800">
                        <div className="font-medium">{row.robot_name}</div>
                        <div className="font-mono text-[11px] text-slate-500">id {row.robot_id}</div>
                      </td>
                      <td className="px-3 py-3 align-top text-xs text-slate-700">
                        {row.miniprogram_id > 0 ? (
                          <>
                            <div className="max-w-[10rem] truncate font-medium" title={row.miniprogram_name}>
                              {row.miniprogram_name || `小程序 #${row.miniprogram_id}`}
                            </div>
                            <div className="font-mono text-[11px] text-slate-500">#{row.miniprogram_id}</div>
                          </>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="max-w-xs px-3 py-3 align-top text-sm text-slate-900 sm:max-w-md">
                        <div className="line-clamp-2" title={row.task_title}>
                          {(row.task_title || '').trim() || '（无标题）'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 align-top font-mono text-xs text-slate-600">#{row.entry_id}</td>
                      <td className="whitespace-nowrap px-3 py-3 align-top font-mono text-xs text-slate-500">{row.forward_task_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > 0 ? (
              <PaginationBar
                className="mt-4"
                page={page}
                totalPages={totalPages}
                total={total}
                summary={`共 ${total} 条，每页 ${PER_PAGE} 条`}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
              />
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
